import { useQuery } from '@tanstack/react-query';
import {
  eachDayOfInterval,
  isWeekend,
  format,
  startOfDay,
  isSunday,
} from 'date-fns';

import { TProgressData } from '../components/SprintTimeline/types';
import { useTeam } from '../contexts/TeamContext';
import { fetchSprintTimeline } from '../services/timeline';
import {
  RawTimelineData,
  SprintCheckInResponse,
  SprintTimelineData,
} from '../types/timeline';
import { getDateWithoutTimezone } from '../utils/timelineUtils';

interface DailyProgress {
  firstCheckIn?: {
    progress: number;
    previousProgress: number;
    hasBlocker?: boolean;
  };
  secondCheckIn?: {
    progress: number;
    previousProgress: number;
    hasBlocker?: boolean;
  };
}

interface TicketCheckIn {
  description?: string;
  blockers?: string[];
  date: string;
  wipLinks?: string;
  currentCompletionPercentage: number;
  previousCompletionPercentage: number;
  created_at: string;
}

export interface GroupedTicketData {
  id: string;
  ticket_number: string;
  title: string;
  externalLink: string;
  currentCompletionPercentage: number;
  checkIns: Record<string, TicketCheckIn[]>;
  created_at: string;
}

export interface AdhocCheckIn {
  id: string;
  title: string;
  description?: string;
  blockers?: string[];
  wipLinks?: string;
  currentCompletionPercentage: number;
  previousCompletionPercentage: number;
}

interface MeetingData {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  duration: number; // in hours
}

export interface GroupedAdhocData {
  [date: string]: AdhocCheckIn[];
}

export interface GroupedMeetingData {
  [date: string]: MeetingData[];
}

const isAfterLastCheckIn = (date: Date, checkInTime: string): boolean => {
  const [hours, minutes] = checkInTime.split(':').map(Number);
  const lastCheckInTime = new Date(date);
  lastCheckInTime.setHours(hours, minutes + 30, 0, 0); // Add 30 min buffer
  return new Date() > lastCheckInTime;
};

const shouldShowMissedCheckIn = (
  date: Date,
  lastCheckInTime: string,
): boolean => {
  const today = new Date();
  const isToday = format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
  const isFuture = date > today;

  if (isFuture) return false;
  if (isToday) return isAfterLastCheckIn(date, lastCheckInTime);
  return true;
};

export function useSprintTimeline(
  memberId?: string,
  employeeId?: string,
  sprintIds?: string[],
  startDate?: string,
  endDate?: string,
  shadowTo?: string,
): {
  data:
    | (SprintTimelineData & {
        groupedTickets: GroupedTicketData[];
        groupedAdhocs: GroupedAdhocData;
        groupedMeetings: GroupedMeetingData;
      })
    | undefined;
  isLoading: boolean;
} {
  const { team } = useTeam();
  return useQuery<
    RawTimelineData,
    Error,
    SprintTimelineData & {
      groupedTickets: GroupedTicketData[];
      groupedAdhocs: GroupedAdhocData;
      groupedMeetings: GroupedMeetingData;
    }
  >({
    queryKey: [
      'sprintTimeline',
      memberId,
      employeeId,
      sprintIds,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchSprintTimeline(
        memberId!,
        employeeId!,
        sprintIds!,
        startDate!,
        endDate!,
        shadowTo,
      ),
    enabled: !!memberId && !!employeeId && !!sprintIds,
    select: (rawData) => {
      const workingDays = eachDayOfInterval({
        start: getDateWithoutTimezone(rawData.startDate),
        end: getDateWithoutTimezone(rawData.endDate),
      }).filter((date) => !isSunday(date));

      // Initialize dayStatus for all working days
      const dayStatus: Record<
        string,
        { type: 'OOO' | 'MISSED' | 'NORMAL'; reason?: string }
      > = {};
      workingDays.forEach((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        dayStatus[dateStr] = { type: 'NORMAL' };
      });

      rawData.meetings?.forEach((meeting) => {
        const meetingStart = startOfDay(new Date(meeting.start_time));
        const meetingEnd = new Date(meeting.end_time);
        const meetingEndDate = startOfDay(meetingEnd);

        // If the meeting ends at the start of the next day, adjust the end date
        const adjustedEndDate =
          meetingEnd.getHours() === 0 && meetingEnd.getMinutes() === 0
            ? new Date(meetingEndDate.getTime() - 1) // Subtract 1 millisecond to make it the previous day
            : meetingEndDate;

        if (
          meeting.title.toLowerCase().includes('ooo') ||
          meeting.title.toLowerCase().includes('out of office')
        ) {
          // Get all days between start and end dates
          eachDayOfInterval({
            start: meetingStart,
            end: adjustedEndDate,
          }).forEach((date) => {
            if (!isWeekend(date)) {
              const dateStr = format(date, 'yyyy-MM-dd');
              dayStatus[dateStr] = { type: 'OOO', reason: meeting.title };
            }
          });
        }
      });
      // override meeting days with leave days if applied on keka
      rawData.leaves.forEach((leave) => {
        if (leave.status === 'approved' || leave.status === 'pending') {
          eachDayOfInterval({
            start: new Date(leave.start_date),
            end: new Date(leave.end_date),
          }).forEach((date) => {
            if (!isWeekend(date)) {
              const dateStr = format(date, 'yyyy-MM-dd');
              dayStatus[dateStr] = { type: 'OOO', reason: 'Leave' };
            }
          });
        }
      });

      // Process check-ins to detecheckins?.[0]ct missed days and override OOO status
      workingDays.forEach((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayCheckIns = rawData.checkIns.filter(
          (checkIn) => checkIn.created_at.split('T')[0] === dateStr,
        );

        if (dayCheckIns.length > 0) {
          dayStatus[dateStr] = { type: 'NORMAL' };
        } else if (
          dayStatus[dateStr]?.type !== 'OOO' &&
          shouldShowMissedCheckIn(day, team?.check_in_times[1] ?? '')
        ) {
          dayStatus[dateStr] = { type: 'MISSED' };
        }
      });

      // Group check-ins by date and sequence
      const dailyProgressByTicket: Record<
        string,
        Record<string, DailyProgress>
      > = {};

      // First, group all check-ins by date and ticket
      rawData.checkIns.forEach((checkIn) => {
        const date = checkIn.created_at.split('T')[0];

        checkIn.ticket_check_ins
          .filter(
            (
              tci,
            ): tci is SprintCheckInResponse['ticket_check_ins'][0] & {
              ticket: NonNullable<typeof tci.ticket>;
            } => tci.check_in_type === 'ticket' && tci.ticket !== null,
          )
          .forEach((tci) => {
            if (!dailyProgressByTicket[date]) {
              dailyProgressByTicket[date] = {};
            }

            if (!dailyProgressByTicket[date][tci.ticket.id]) {
              dailyProgressByTicket[date][tci.ticket.id] = {};
            }

            // Store progress based on check-in sequence
            if (checkIn.daily_sequence === 1) {
              dailyProgressByTicket[date][tci.ticket.id].firstCheckIn = {
                progress: tci.completion_percentage,
                previousProgress: tci.previous_completion_percentage,
                hasBlocker: Boolean(tci.blockers),
              };
            } else {
              dailyProgressByTicket[date][tci.ticket.id].secondCheckIn = {
                progress: tci.completion_percentage,
                previousProgress: tci.previous_completion_percentage,
                hasBlocker: Boolean(tci.blockers),
              };
            }
          });
      });

      // Initialize progress data for all tickets and days
      const progressData: Record<string, TProgressData> = {};

      // Initialize empty progress for all tickets on all days
      workingDays.forEach((day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        rawData.tickets.forEach((ticket) => {
          progressData[`${dateStr}-${ticket.id}`] = {
            progress: null,
            previousProgress: null,
          };
        });
      });

      // Then update with actual check-in data
      Object.entries(dailyProgressByTicket).forEach(
        ([date, ticketProgresses]) => {
          Object.entries(ticketProgresses).forEach(
            ([ticketId, dailyProgress]) => {
              const finalProgress =
                dailyProgress.secondCheckIn || dailyProgress.firstCheckIn;
              if (finalProgress) {
                progressData[`${date}-${ticketId}`] = {
                  progress: finalProgress.progress,
                  previousProgress:
                    dailyProgress.firstCheckIn?.previousProgress !== undefined
                      ? dailyProgress.firstCheckIn.previousProgress
                      : finalProgress.previousProgress,
                  hasBlocker: finalProgress.hasBlocker,
                };
              }
            },
          );
        },
      );

      // Use the fetched tickets directly instead of collecting from check-ins
      const sortedTickets = rawData.tickets.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      // Create grouped ticket data
      const groupedTicketData: Record<string, GroupedTicketData> = {};

      // Initialize grouped ticket data
      rawData.tickets.forEach((ticket) => {
        groupedTicketData[ticket.id] = {
          id: ticket.id,
          ticket_number: ticket.ticket_number,
          title: ticket.title,
          externalLink: ticket.external_link,
          currentCompletionPercentage: ticket.completion_percentage,
          created_at: ticket.created_at,
          checkIns: {},
        };
      });

      // Process check-ins for grouped data
      rawData.checkIns.forEach((checkIn) => {
        const date = checkIn.created_at.split('T')[0];

        checkIn.ticket_check_ins
          .filter(
            (
              tci,
            ): tci is SprintCheckInResponse['ticket_check_ins'][0] & {
              ticket: NonNullable<typeof tci.ticket>;
              description?: string;
              wip_links?: string;
            } => tci.check_in_type === 'ticket' && tci.ticket !== null,
          )
          .forEach((tci) => {
            const ticketId = tci.ticket.id;
            const checkInData: TicketCheckIn = {
              description: tci.description,
              blockers: tci.blockers ? [tci.blockers] : undefined,
              date,
              wipLinks: tci.wip_links,
              currentCompletionPercentage: tci.completion_percentage,
              previousCompletionPercentage: tci.previous_completion_percentage,
              created_at: checkIn.created_at,
            };

            if (!groupedTicketData[ticketId].checkIns[date]) {
              groupedTicketData[ticketId].checkIns[date] = [];
            }
            groupedTicketData[ticketId].checkIns[date].push(checkInData);
          });
      });

      // Sort check-ins by date and update current completion percentage for grouped data
      const groupedTickets = Object.values(groupedTicketData);

      // Create grouped adhoc data
      const groupedAdhocData: GroupedAdhocData = {};

      // Process check-ins for adhoc tasks
      rawData.checkIns.forEach((checkIn) => {
        const date = checkIn.created_at.split('T')[0];

        checkIn.ticket_check_ins
          .filter(
            (
              tci,
            ): tci is SprintCheckInResponse['ticket_check_ins'][0] & {
              description?: string;
              wip_links?: string;
              title: string;
            } => tci.check_in_type === 'adhoc_task',
          )
          .forEach((tci) => {
            const adhocCheckIn: AdhocCheckIn = {
              id: tci.id,
              title: tci.title,
              description: tci.description,
              blockers: tci.blockers ? [tci.blockers] : undefined,
              wipLinks: tci.wip_links,
              currentCompletionPercentage: tci.completion_percentage,
              previousCompletionPercentage: tci.previous_completion_percentage,
            };

            if (!groupedAdhocData[date]) {
              groupedAdhocData[date] = [];
            }
            groupedAdhocData[date].push(adhocCheckIn);
          });
      });

      // Process meetings (excluding those >= 24 hours)
      const groupedMeetings: GroupedMeetingData = {};

      rawData.meetings
        ?.filter((meeting) => {
          const start = new Date(meeting.start_time);
          const end = new Date(meeting.end_time);
          const durationHours =
            (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          return durationHours < 24;
        })
        .forEach((meeting) => {
          const start = new Date(meeting.start_time);
          const end = new Date(meeting.end_time);
          const durationHours =
            (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          const date = format(start, 'yyyy-MM-dd');

          const meetingData: MeetingData = {
            id: meeting.id,
            title: meeting.title,
            startTime: meeting.start_time,
            endTime: meeting.end_time,
            duration: durationHours,
          };

          if (!groupedMeetings[date]) {
            groupedMeetings[date] = [];
          }
          groupedMeetings[date].push(meetingData);
        });
      return {
        startDate: rawData.startDate,
        endDate: rawData.endDate,
        workingDays,
        tickets: sortedTickets.map((ticket) => ({
          ...ticket,
        })),
        progressData,
        dayStatus,
        groupedTickets: groupedTickets.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ),
        groupedAdhocs: groupedAdhocData,
        groupedMeetings,
      };
    },
  });
}
